import React, { useMemo } from 'react';
import { Button, DatePicker, Input, InputNumber, Select, Space } from 'antd';
import { getTreeSelectVersionOptions } from 'utils/getTreeSelectVersionOptions';
import VersionHybridSelect from 'components/commons/VersionHybridSelect';
import {
  ERROR_EXCEPTION_TYPE_LIST_CUSTOM_ERROR_OPTIONS, HANG_EXCEPTION_TYPE_LIST_OPTIONS,
  JANK_EXCEPTION_TYPE_LIST_OPTIONS,
  JANK_V2_EXCEPTION_TYPE_OPTION,
  TapdBugStatusOptions
} from 'utils/constants';
import DurationRangeInput from 'components/commons/IssueCrashFilter/DurationRangeInput';
import RamSelect from 'components/commons/IssueCrashFilter/RamSelect';
import { ze } from 'utils/zhEn';
import IosDeviceSelect from 'components/commons/IssueCrashFilter/IosDeviceSelect';
import {
  AllFilterFields, CaseSensitivity,
  FieldName,
  IssueCrashFilterExUtil,
  QueryType,
} from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { useTranslation } from 'react-i18next';
import RamGibSelect from 'components/commons/IssueCrashFilter/RamGibSelect';
import AbsoluteAndRelativeDateRange from 'components/commons/IssueCrashFilter/AbsoluteAndRelativeDateRange';
import VersatileKeywordSearch from 'components/commons/IssueCrashFilter/VersatileKeywordSearch';
import VersatileTextSearch from 'components/commons/IssueCrashFilter/VersatileTextSearch';
import EnlargeableInputTerms from 'components/commons/IssueCrashFilter/EnlargeableInputTerms';
import { COUNTRY_LIST_OPTIONS } from 'components/exception/ProductSummaryPage/CountrySelect';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import FeatureTagSelect from 'components/commons/IssueCrashFilter/FeatureTagSelect';
import AndroidDeviceSelect from 'components/commons/IssueCrashFilter/AndroidDeviceSelect';
import { useSelector } from 'react-redux';
import { selectNullableServerAppSettings } from 'utils/selectors/selectors';
import { ServerAppSettings } from 'utils/server-app-settings';
import VersionWithRangeFilter from 'components/commons/IssueCrashFilter/VersionWithRangeFilter';
import { getExceptionIconByType } from 'utils/exception-icon';
import { CsSelect } from 'components/antd-extension/CsSelect';
import ProcessorSearch from 'components/commons/IssueCrashFilter/ProcessorSearch';
import IssueStatusSelect from 'components/commons/IssueCrashFilter/IssueStatusSelect';
import { isAndroid, isPc, isWechatMiniProgram, PlatformUtil } from 'utils/platform';
import MacDeviceSelect from 'components/commons/IssueCrashFilter/MacDeviceSelect';
import OsVersionSearch from 'components/commons/IssueCrashFilter/OsVersionSearch';
import { VmTypeUtil } from 'utils/constants/vm-type';
import YesNoAllSelect from 'components/commons/IssueCrashFilter/YesNoAllSelect';
import PcDeviceSelect from 'components/commons/IssueCrashFilter/PcDeviceSelect';
import NumberRangeFilter from 'components/commons/IssueCrashFilter/NumberRangeFilter';
import UserIdByAliasSearch from 'components/commons/IssueCrashFilter/UserIdByAliasSearch';
import IcfVmTypeSelect from 'components/commons/IssueCrashFilter/IcfVmTypeSelect';
import IcfCpuArchSelect from 'components/commons/IssueCrashFilter/IcfCpuArchSelect';
import IcfCustomKvFilter from 'components/commons/IssueCrashFilter/IcfCustomKvFilter';
import IcfInNotInMultiSelect from 'components/commons/IssueCrashFilter/IcfInNotInMultiSelect';
import AppSoNameWhitelistSearch from 'components/commons/IssueCrashFilter/AppSoNameWhitelistSearch';
import IssueAiStatusSelect from 'components/commons/IssueCrashFilter/IssueAiStatusSelect';
import IcfGameMatrixFieldSelect from 'components/commons/IssueCrashFilter/IcfGameMatrixFieldSelect';
import UserSceneTagSelect from 'components/exception/ProductSummaryPage/UserSceneTagSelect';

/**
 * FieldValuePart：IssueCrashFilterEx 里"单条过滤条件"除字段选择之外的"值输入"部分。
 *
 * 根据当前字段（name）的类型，分派渲染对应的输入组件，例如：
 *   - 版本、上报时间等使用专门的组件（VersionWithRangeFilter、AbsoluteAndRelativeDateRange 等）；
 *   - 多选枚举类字段（如 channelId / bundleId / cpuArch 等）使用 IcfInNotInMultiSelect（带"包含/不包含"切换）；
 *   - 文本搜索类字段使用 VersatileKeywordSearch / VersatileTextSearch / EnlargeableInputTerms 等；
 *   - 数值区间使用 NumberRangeFilter；其他还有各类设备、机型、OS 版本等专用选择器。
 * 通过 updateValue 向上回传新的字段值（形如 { queryType, not, term, terms, gt/gte/lt/lte, ... }）。
 *
 * 新增字段时，一般只需要在本组件里加一个对应的分支即可。
 */
const FieldValuePart = ({
  name,
  value,
  appId,
  platformId,
  filterOptions,
  isReadOnly,
  isAdvancedTrend, // 是否是版本趋势页面
  isFeatureConfigRealtimeTag,
  updateValue = () => {},
}) => {
  const { t } = useTranslation();

  const serverAppSettings = useSelector(reduxState => selectNullableServerAppSettings(reduxState, appId)) || {};
  const enableJank = !!serverAppSettings[ServerAppSettings.keys.enableJank];
  const enableHang = !!serverAppSettings[ServerAppSettings.keys.enableHang];
  const enableMetricKit = !!serverAppSettings[ServerAppSettings.keys.enableMetricKit];
  const enableCustomErrorTypes = !!serverAppSettings[ServerAppSettings.keys.enableCustomErrorTypes];
  const enableThreadMonitor = !!serverAppSettings[ServerAppSettings.keys.enableThreadMonitor];
  const enableRegexpQueryType = !!serverAppSettings[ServerAppSettings.keys.enableRegexpQueryType];
  const isSuper = useSelector(reduxState => !!reduxState.user.get('current').toJS().isSuper);

  const {
    subField,
    not,
    queryType,
    term,
    terms,
    gt,
    gte,
    lt,
    lte,
    legacyValue,
  } = value || {};

  const enrichedFilterOptions = useMemo(() => {
    if (!filterOptions || !filterOptions.exceptionTypeList) {
      return filterOptions || {};
    }
    return {
      ...filterOptions,
      exceptionTypeList: [
        ...filterOptions.exceptionTypeList,
        ...(enableCustomErrorTypes ? ERROR_EXCEPTION_TYPE_LIST_CUSTOM_ERROR_OPTIONS : []),
        ...(enableJank ? JANK_EXCEPTION_TYPE_LIST_OPTIONS : []),
        ...(enableHang ? HANG_EXCEPTION_TYPE_LIST_OPTIONS : []),
        ...(enableThreadMonitor ? [JANK_V2_EXCEPTION_TYPE_OPTION] : []),
      ],
    }
  }, [filterOptions, enableCustomErrorTypes, enableJank, enableHang, enableThreadMonitor]);

  const fieldInfo = AllFilterFields.find(x => x.name === name) || {};
  const {
    filterOptionProperty,
    isMultipleSelect,
    isVersatileKeywordSearch,
    searchMethodWhitelist,
    defaultQueryType,
    isVersatileTextSearch,
    hasIsEmptyQueryType,
  } = fieldInfo;

  if (!name) {
    return <Input
      disabled={true}
      style={{ width: '100%' }}
    />;
  }

  if (FieldName.version === name || FieldName.issueFirstCrashVersion === name) {
    const versionTree = getTreeSelectVersionOptions(enrichedFilterOptions.version || []);
    return <VersionWithRangeFilter
      isReadOnly={isReadOnly}
      style={{ width: '100%' }}
      treeData={versionTree}
      value={value}
      onChange={(v) => {
        updateValue(v);
      }}
    />;
  }

  if (FieldName.exceptionType === name) {
    return <CsSelect
      style={{ width: '100%', fontSize: '14px' }}
      mode='multiple'
      allowClear
      showSearch={false}
      options={enrichedFilterOptions[filterOptionProperty || name]}
      placeholder={fieldInfo.placeholder}
      value={ terms }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    />;
  }

  if (FieldName.issueStatus === name) {
    return <IssueStatusSelect
      style={{ width: '100%', fontSize: '14px' }}
      isMultiple={true}
      value={ terms }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    />;
  }

  if (FieldName.issueAiStatus === name) {
    return <IssueAiStatusSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />;
  }

  if (FieldName.hasHackedModule === name) {
    return <Select
      style={{ width: '100%', fontSize: '14px' }}
      allowClear
      options={[
        { label: ze('不限', 'All'), value: null },
        { label: ze('存在', 'Exists'), value: 1 },
        { label: ze('不存在', 'Not Exists'), value: 0 },
      ]}
      placeholder={ze('不限', 'All')}
      value={ term }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERM, term: v });
      }}
    />;
  }

  if (FieldName.exceptionCategory === name) {
    const options = ExceptionCategoryUtil.getAvailableOptions(platformId, enableJank, enableHang, enableMetricKit, isSuper);
    return <CsSelect
      style={{ width: '100%', fontSize: '14px' }}
      mode='multiple'
      allowClear
      showSearch={false}
      placeholder={fieldInfo.placeholder}
      value={ terms }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    >
      {
        options.map((option) => {
          const icon = getExceptionIconByType({exceptionType: option.value, size: 14});
          return <Option key={option.value} value={option.value}>
            <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
              {icon}
              {option.label}
            </div>
          </Option>;
        })
      }
    </CsSelect>;
  }

  if (name === FieldName.issueTag) {
    return <div style={{ display: 'flex' }}>
      <Select
        style={{ width: '120px' }}
        options={[
          { label: t('issueCrashFilter.包含'), value: 0 },
          { label: t('issueCrashFilter.不包含'), value: 1 },
        ]}
        value={ Number(!!not) }
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          updateValue({
            ...value,
            not: !!v,
          });
        }}
      />
      <CsSelect
        style={{ flexGrow: 1, fontSize: '14px' }}
        mode='multiple'
        allowClear
        showSearch={true}
        optionFilterProp='label'
        options={filterOptions[filterOptionProperty || name]}
        placeholder={fieldInfo.placeholder}
        value={ terms }
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          updateValue({ queryType: QueryType.TERMS, terms: v, not });
        }}
      />
    </div>
  }

  if (FieldName.featureTag === name) {
    return <div style={{ display: 'flex' }}>
      <Select
        style={{ width: '120px', flexShrink: 0 }}
        options={[
          { label: t('issueCrashFilter.包含'), value: 0 },
          { label: t('issueCrashFilter.不包含'), value: 1 },
        ]}
        value={ Number(!!not) }
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          updateValue({
            ...value,
            not: !!v,
          });
        }}
      />
      <FeatureTagSelect
        appId={appId}
        isReadOnly={isReadOnly}
        isMultiSelect={true}
        value={ terms }
        onChange={(v) => {
          updateValue({ queryType: QueryType.TERMS, terms: v });
        }}
      />
    </div>;
  }

  if (FieldName.userIdByAlias === name) {
    return <UserIdByAliasSearch
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
      isReadOnly={isReadOnly}
    />
  }

  if (FieldName.processor === name) {
    return <ProcessorSearch
      options={filterOptions[filterOptionProperty || name]}
      value={value}
      onChange={v => updateValue(v)}
      isReadOnly={isReadOnly}
    />
  }

  if (FieldName.issueReportCount === name || FieldName.issueDeviceCount === name) {
    return <NumberRangeFilter
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />
  }

  if ([
    ...(IssueCrashFilterExUtil.getIsBundleIdOptionsAvailable(appId) ? [FieldName.bundleId] : []),
    FieldName.channelId,
  ].includes(name)) {
    return <IcfInNotInMultiSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={(v) => updateValue(v)}
      options={filterOptions[filterOptionProperty || name]}
      placeholder={fieldInfo.placeholder}
    />
  }

  if (FieldName.vmStatus === name) {
    return <Select
      style={{ width: '100%', fontSize: '14px' }}
      allowClear
      options={[
        { label: t('issueCrashFilter.anyPlaceholder'), value: 'ALL' },
        { label: t('EXCP_OVERVIEW.仅真机'), value: 'ONLY_NOT_VM' },
        { label: t('EXCP_OVERVIEW.仅模拟器'), value: 'ONLY_VM' },
      ]}
      placeholder={t('issueCrashFilter.anyPlaceholder')}
      value={ term }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERM, term: v });
      }}
    />
  }

  if (FieldName.vmType === name) {
    return <IcfVmTypeSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />
  }

  if (FieldName.cpuArch === name) {
    return <IcfCpuArchSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />
  }

  if (FieldName.appInBack === name) {
    return <YesNoAllSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
      yesLabel={t('REPORTDETAIL.background')}
      noLabel={t('REPORTDETAIL.foreground')}
    />;
  }

  if (FieldName.isSecondReport === name) {
    return <YesNoAllSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
      yesLabel={t('REPORTDETAIL.secondReportTrue')}
      noLabel={t('REPORTDETAIL.secondReportFalse')}
    />;
  }

  if (FieldName.isNetbarMachine === name) {
    return <YesNoAllSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />;
  }

  if (FieldName.appBit === name) {
    return <CsSelect
      style={{ width: '100%', fontSize: '14px' }}
      mode='multiple'
      allowClear
      options={[
        { label: ze('32位', '32 bit'), value: 32 },
        { label: ze('64位', '64 bit'), value: 64 },
        { label: ze('未识别', 'Unknown'), value: 0 },
      ]}
      placeholder={t('issueCrashFilter.anyPlaceholder')}
      value={ terms }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    />
  }

  if (FieldName.systemExitStatus === name) {
    return <Select
      style={{ width: '100%', fontSize: '14px' }}
      allowClear
      options={[
        { label: t('issueCrashFilter.anyPlaceholder'), value: 'ALL' },
        { label: t('common.yes'), value: 'ONLY_SYSTEM_EXIT' },
        { label: t('common.no'), value: 'ONLY_NOT_SYSTEM_EXIT' },
      ]}
      placeholder={fieldInfo.placeholder}
      value={ term }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERM, term: v });
      }}
    />
  }

  if (FieldName.startupCrash === name || FieldName.startupHang === name) {
    return <YesNoAllSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />;
  }

  if (FieldName.tapdBugStatus === name || FieldName.jiraBugStatus === name) {
    return <Select
      style={{ width: '100%', fontSize: '14px' }}
      options={TapdBugStatusOptions}
      placeholder={fieldInfo.placeholder}
      value={ term }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERM, term: v });
      }}
    />
  }

  if (FieldName.oomStatus === name) {
    return <Select
      style={{ width: '100%', fontSize: '14px' }}
      allowClear
      options={[
        { label: t('issueCrashFilter.anyPlaceholder'), value: 'ALL' },
        { label: t('issueCrashFilter.仅OOM崩溃'), value: 'ONLY_IS_OOM' },
        { label: t('issueCrashFilter.仅非OOM崩溃'), value: 'ONLY_NOT_IS_OOM' },
      ]}
      placeholder={fieldInfo.placeholder}
      value={ term }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERM, term: v });
      }}
    />
  }

  if (FieldName.ramGib === name) {
    return <RamGibSelect
    platformId={platformId}
    value={ terms }
    onChange={(v) => {
      if (isReadOnly) {
        return;
      }
      updateValue({ queryType: QueryType.TERMS, terms: v });
    }}
  />
  }

  if ([FieldName.crashUploadTime, FieldName.crashTime, FieldName.issueFirstUploadTime, FieldName.issueUploadTime, FieldName.startMillis].includes(name)) {
    return <AbsoluteAndRelativeDateRange
      value={value}
      onChange={(v) => updateValue(v)}
      isReadOnly={isReadOnly}
    />;
  }

  if ((PlatformUtil.isMac(platformId) && FieldName.model === name)) {
    return <MacDeviceSelect
      appId={appId}
      isReadOnly={isReadOnly}
      value={terms}
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        // 当前大数据那边提供的选项和ES实际数据大小写不一致。因此要强制设置为大小写不敏感的方式匹配，下面几个model字段也一样
        updateValue({ queryType: QueryType.TERMS, terms: v, caseSensitivity: CaseSensitivity.INSENSITIVE });
      }}
    />;
  }

  if (FieldName.androidModel === name) {
    return <AndroidDeviceSelect
      appId={appId}
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue({ ...v, caseSensitivity: CaseSensitivity.INSENSITIVE })}
    />;
  }

  if (FieldName.iosModel === name) {
    return <IosDeviceSelect
      appId={appId}
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue({ ...v, caseSensitivity: CaseSensitivity.INSENSITIVE })}
    />;
  }

  if (FieldName.model === name && isPc(platformId, undefined)) {
    return <PcDeviceSelect
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue({ ...v, caseSensitivity: CaseSensitivity.INSENSITIVE })}
    />;
  }

  if (FieldName.model === name && isWechatMiniProgram(platformId)) {
    return <AndroidDeviceSelect
      appId={appId}
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue({ ...v, caseSensitivity: CaseSensitivity.INSENSITIVE })}
    />;
  }

  if (isAndroid(platformId) && FieldName.osVersion === name) {
    return <OsVersionSearch
      appId={appId}
      isReadOnly={isReadOnly}
      value={value}
      onChange={v => updateValue(v)}
    />;
  }

  if (isAdvancedTrend && FieldName.userSceneTag === name) {
    return <UserSceneTagSelect
      isMultiple={true}
      value={terms}
      style={{ width: '100%' }}
      onChangeWhenDropdownClosed={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    />;
  }

  if (!isAdvancedTrend && FieldName.userSceneTag === name) {
    return <VersatileKeywordSearch
      defaultQueryType={QueryType.TERMS}
      hasIsEmptyQueryType={hasIsEmptyQueryType}
      value={value}
      onChange={v => updateValue(v)}
      isReadOnly={isReadOnly}
      queryTypeTermsCustomComponent={({ value: termsValue, onChange: termsOnChange }) => (
        <UserSceneTagSelect
          isMultiple={true}
          value={termsValue}
          style={{ flexGrow: 1 }}
          onChangeWhenDropdownClosed={termsOnChange}
        />
      )}
    />;
  }

  if (isVersatileKeywordSearch
    || (FieldName.bundleId === name && !IssueCrashFilterExUtil.getIsBundleIdOptionsAvailable(appId))
    || (isVersatileTextSearch && isFeatureConfigRealtimeTag)
  ) {
    const hasRegexpQueryType = isFeatureConfigRealtimeTag && enableRegexpQueryType;
    return <VersatileKeywordSearch
      defaultQueryType={defaultQueryType}
      searchMethodWhitelist={searchMethodWhitelist}
      hasIsEmptyQueryType={hasIsEmptyQueryType}
      hasRegexpQueryType={hasRegexpQueryType}
      value={value}
      onChange={v => updateValue(v)}
      isReadOnly={isReadOnly}
    />
  }

  if (isVersatileTextSearch && !isFeatureConfigRealtimeTag) {
    return <VersatileTextSearch
      value={value}
      onChange={v => updateValue(v)}
      isReadOnly={isReadOnly}
    />
  }

  if (FieldName.elapsedTime === name) {
    return <DurationRangeInput
      value={ [gte, lte] }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.RANGE, gte: v[0], lte: v[1] });
      }}
    />;
  }

  if (FieldName.ram === name) {
    return <NumberRangeFilter
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
      multiplier={1024 * 1024 * 1024}
      addonAfter={'GiB'}
    />
  }

  if (FieldName.countryOrRegion === name) {
    return <CsSelect
      style={{ width: '100%', fontSize: '14px' }}
      mode='multiple'
      allowClear
      showSearch={true}
      optionFilterProp='label'
      options={COUNTRY_LIST_OPTIONS}
      value={ terms }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        updateValue({ queryType: QueryType.TERMS, terms: v });
      }}
    />
  }

  // 先锋云游戏特有字段
  if ([FieldName.gmHardware, FieldName.gmCluster, FieldName.gmGameId, FieldName.gmGameCluster].includes(name)) {
    return <IcfGameMatrixFieldSelect
      appId={appId}
      fieldName={name}
      isReadOnly={isReadOnly}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />;
  }

  if (FieldName.customKey === name) {
    return <IcfCustomKvFilter
      appId={appId}
      isReadOnly={isReadOnly}
      isAdvancedTrend={isAdvancedTrend}
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
    />;
  }

  if (FieldName.appSoNameWhitelist === name) { // iGame定制需求
    return <AppSoNameWhitelistSearch
      fieldValue={value}
      onChangeFieldValue={v => updateValue(v)}
      isReadOnly={isReadOnly}
    />
  }

  // fallback
  return <Input
    disabled={true}
    style={{ width: '100%' }}
    value={JSON.stringify(value)}
  />;
};

export default FieldValuePart;
